import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Send, CheckCircle2, User, Mail, Phone, Building2, MessageSquare } from 'lucide-react';
import { useToast } from '../hooks/use-toast';

const SERVICE_OPTIONS = [
  'Shared Hosting', 'Zimbra Email Hosting', 'WordPress Hosting',
  'Virtual Private Server', 'Cloud Server', 'Dedicated Server',
  'Backup as a Service', 'Colocation Rack', 'SSL Certificate',
  'AWS / Azure / GCP Cloud', 'Cyber Security Solutions',
  'Tally on Cloud', 'TrueConf Video Conferencing',
  'Managed IT Services', 'Custom Enterprise Solution'
];

const QueryFormModal = ({ open, onClose, onSuccess, title = 'Unlock Pricing', subtitle = 'Fill in your details to reveal our transparent pricing plans.', defaultService = '' }) => {
  const { toast } = useToast();
  const [form, setForm] = useState({ name: '', email: '', phone: '', company: '', service: defaultService || SERVICE_OPTIONS[0], message: '' });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (open && defaultService) {
      setForm((f) => ({ ...f, service: defaultService }));
      setDone(false);
    }
  }, [open, defaultService]);

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone) {
      toast({ title: 'Missing details', description: 'Name, email and phone are required.' });
      return;
    }
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(form.email)) {
      toast({ title: 'Invalid email', description: 'Please provide a valid email address.' });
      return;
    }
    setLoading(true);
    try {
      const res = await fetch('/api/quote', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      const data = await res.json();
      setLoading(false);
      if (data.success) {
        setDone(true);
        try {
          localStorage.setItem('dc10g_pricing_unlocked', '1');
        } catch (e) {}
        toast({ title: 'Thanks! Your request is submitted', description: 'Our team will contact you within 24 hours.' });
        setTimeout(() => {
          onSuccess && onSuccess();
          onClose && onClose();
        }, 1400);
      } else {
        toast({ title: 'Something went wrong', description: data.message || 'Please try again in a moment.' });
      }
    } catch (err) {
      setLoading(false);
      toast({ title: 'Network error', description: 'Could not reach the server. Please try again.' });
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          className="fixed inset-0 z-[95] flex items-center justify-center p-4 bg-slate-950/70 backdrop-blur-sm"
          onClick={onClose}
        >
          <motion.div
            initial={{ scale: 0.9, y: 40, opacity: 0 }} animate={{ scale: 1, y: 0, opacity: 1 }} exit={{ scale: 0.9, y: 40, opacity: 0 }}
            transition={{ type: 'spring', damping: 24 }}
            className="relative w-full max-w-2xl rounded-3xl bg-white shadow-2xl overflow-hidden max-h-[95vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="relative p-6 pb-5 bg-gradient-to-br from-sky-600 via-blue-600 to-indigo-700 text-white overflow-hidden">
              <div className="absolute inset-0 grid-bg opacity-30" />
              <div className="absolute -top-10 -right-10 w-40 h-40 rounded-full bg-cyan-400/30 blur-3xl" />
              <button onClick={onClose} className="absolute top-4 right-4 w-9 h-9 rounded-full bg-white/15 hover:bg-white/25 flex items-center justify-center transition-colors">
                <X size={16} />
              </button>
              <div className="relative">
                <div className="text-[10px] font-semibold tracking-[0.3em] text-cyan-200 uppercase mb-1">DC10G · Custom Quote</div>
                <h3 className="text-2xl md:text-3xl font-extrabold">{title}</h3>
                <p className="mt-2 text-sky-100 text-sm">{subtitle}</p>
              </div>
            </div>

            <div className="p-6">
              {done ? (
                <motion.div initial={{ scale: 0.8, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} className="text-center py-6">
                  <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-emerald-100 mb-4">
                    <CheckCircle2 className="text-emerald-600" size={32} />
                  </div>
                  <h4 className="text-xl font-bold text-slate-900">Thanks! We received your request</h4>
                  <p className="mt-2 text-slate-600 text-sm">Our team will reach you at <span className="font-semibold text-slate-800">{form.email}</span> within 24 hours.</p>
                </motion.div>
              ) : (
                <form onSubmit={submit} className="space-y-4">
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <label className="text-xs font-semibold text-slate-600 mb-1.5 block">Full Name *</label>
                      <div className="relative">
                        <User size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                        <input type="text" value={form.name} onChange={update('name')} placeholder="John Doe" className="input-clean pl-10" required />
                      </div>
                    </div>
                    <div>
                      <label className="text-xs font-semibold text-slate-600 mb-1.5 block">Company</label>
                      <div className="relative">
                        <Building2 size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                        <input type="text" value={form.company} onChange={update('company')} placeholder="Acme Inc." className="input-clean pl-10" />
                      </div>
                    </div>
                  </div>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <label className="text-xs font-semibold text-slate-600 mb-1.5 block">Email *</label>
                      <div className="relative">
                        <Mail size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                        <input type="email" value={form.email} onChange={update('email')} placeholder="you@company.com" className="input-clean pl-10" required />
                      </div>
                    </div>
                    <div>
                      <label className="text-xs font-semibold text-slate-600 mb-1.5 block">Phone *</label>
                      <div className="relative">
                        <Phone size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                        <input type="tel" value={form.phone} onChange={update('phone')} placeholder="+91 98765 43210" className="input-clean pl-10" required />
                      </div>
                    </div>
                  </div>
                  <div>
                    <label className="text-xs font-semibold text-slate-600 mb-1.5 block">Service Interested In</label>
                    <select value={form.service} onChange={update('service')} className="input-clean">
                      {SERVICE_OPTIONS.map((s) => (<option key={s} value={s}>{s}</option>))}
                    </select>
                  </div>
                  <div>
                    <label className="text-xs font-semibold text-slate-600 mb-1.5 block">Message (optional)</label>
                    <div className="relative">
                      <MessageSquare size={16} className="absolute left-3 top-3 text-slate-400" />
                      <textarea value={form.message} onChange={update('message')} rows="3" placeholder="Tell us about your requirement..." className="input-clean pl-10 pt-3 resize-none" />
                    </div>
                  </div>
                  <button type="submit" disabled={loading} className="btn-primary w-full justify-center py-3 disabled:opacity-70">
                    {loading ? 'Submitting...' : <>Submit Request <Send size={16} /></>}
                  </button>
                  <p className="text-[11px] text-center text-slate-500">By submitting, you agree to be contacted by DC10G. We respect your privacy.</p>
                </form>
              )}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default QueryFormModal;
